<template>
  <section v-if="showSettings" class="wrapper">
    <button class="ex-button" @click="handleSettings">
      <span class="visually-hidden">close</span>
      <svg viewBox="0 0 18 18">
        <path d="M13.2095 4.89523L4.99998 13.1048" />
        <path d="M13.2095 13.1048L4.99998 4.89525" />
      </svg>
    </button>
    <h1>Settings</h1>
    <div>There's nothing here but soon you'll be able to set custom clocks and save them to your presets.</div>
  </section>
  <section v-else-if="showInfo">
    <button class="ex-button" @click="handleInfo">
      <span class="visually-hidden">close</span>
      <svg viewBox="0 0 18 18">
        <path d="M13.2095 4.89523L4.99998 13.1048" />
        <path d="M13.2095 13.1048L4.99998 4.89525" />
      </svg>
    </button>
    <chess-info :gameStarted="gameStarted" @newGame="handleNewGame" />
  </section>
  <section v-else-if="showEnd">
    <button class="ex-button" @click="handleEnd">
      <span class="visually-hidden">close</span>
      <svg viewBox="0 0 18 18">
        <path d="M13.2095 4.89523L4.99998 13.1048" />
        <path d="M13.2095 13.1048L4.99998 4.89525" />
      </svg>
    </button>
    <chess-end :notation="notation" @hardReset="hardReset" @resume="showEnd = false" @save="handleReset" />
  </section>
  <section v-show="!showSettings && !showInfo && !showEnd">
    <div class="almost-half">
      <chess-clock 
        :start-time="topClock.base * 10" 
        :bonus-time="topClock.bonus"
        :gameStarted="gameStarted" 
        :running="!paused && topRunning" 
        :disabled="gameStarted && (paused || !topRunning)"
        class="flip no-sleep"
        :class="[{'chess-clock--minimized': topNotating}, {'almost-half': !topNotating}, {'chess-clock--cramped': topCramped}]"
        @click="handleTimer('top')" 
      />
      <chess-notation 
        v-show="topNotating" 
        :is-valid="gameStarted && white === 'top' ? whiteIsValid : blackIsValid"
        :color="gameStarted && white === 'top' ? 'white' : 'black'" 
        class="flip almost-half"
        @isCramped="handleCramped('top', $event)"
        @validate="validate($event)"
        @notate="handleNotation('top', $event)" 
      />
    </div>

    <chess-controls 
      :gameStarted="gameStarted" 
      :is-paused="paused" 
      @togglePause="handlePause" 
      @hardReset="hardReset" 
      @info="handleInfo" 
      @settings="handleSettings" 
      @stop="handleEnd"
    />

    <div class="almost-half">
      <chess-clock
        :start-time="bottomClock.base * 10" 
        :bonus-time="bottomClock.bonus"
        :gameStarted="gameStarted" 
        :running="!paused && bottomRunning" 
        :disabled="gameStarted && (paused || !bottomRunning)" 
        class="no-sleep"
        :class="[{'chess-clock--minimized': bottomNotating}, {'almost-half': !bottomNotating}, {'chess-clock--cramped': bottomCramped}]"
        @click="handleTimer('bottom')" 
      />
      <chess-notation 
        v-show="bottomNotating" 
        :color="gameStarted && white === 'bottom' ? 'white' : 'black'" 
        :is-valid="gameStarted && white === 'bottom' ? whiteIsValid : blackIsValid"
        class="almost-half" 
        @isCramped="handleCramped('bottom', $event)"
        @validate="validate($event)"
        @notate="handleNotation('bottom', $event)" 
      />
    </div>
  </section>
  
</template>

<script>
import ChessClock from './components/ChessClock.vue';
import ChessControls from './components/ChessControls.vue';
import ChessNotation from './components/ChessNotation.vue';
import ChessInfo from './components/ChessInfo.vue';
import ChessEnd from './components/ChessEnd.vue';
import NoSleep from '@uriopass/nosleep.js';
import { Chess } from 'chess.js';

import './assets/styles.css';

export default {
  name: 'App',
  components: {
    ChessClock,
    ChessControls,
    ChessNotation,
    ChessInfo,
    ChessEnd
  },
  data() {
    return {
      gameStarted: false,
      paused: true,
      topRunning: false,
      bottomRunning: true,
      topNotating: false,
      bottomNotating: false,
      white: undefined,
      whiteMoves: [],
      blackMoves: [],
      notationHead: null,
      notationBody: null,
      notationTurn: 'white',
      topClock: {
        base: 600,
        bonus: 0
      },
      bottomClock: {
        base: 600,
        bonus: 0
      },
      showSettings: false,
      showInfo: false,
      showEnd: false,
      noSleepIsEnabled: false,
      timeControl: undefined,
      chessJsGame: null,
      whiteIsValid: true,
      blackIsValid: true,
      pendingMove: null,
      topCramped: false,
      bottomCramped: false,
      existingNotation: null
    }
  },  
  created() {
      this.timeControl = window.location.hash.replace('#', '');
      this.setClocks(this.timeControl);
      this.setPgnMetadata();
      this.chessJsGame = new Chess();
  },
  mounted() {
    const noSleep = new NoSleep();
    document.querySelector('.no-sleep').addEventListener('click', function enableNoSleep() {
        noSleep.enable();
        this.noSleepIsEnabled = true;
    }, false);
  },
  methods: {
    setClocks(timeString) {
      this.topClock.base = +timeString.split('+')[0];
      this.bottomClock.base = +this.topClock.base;

      if(timeString.includes('+')) {
        this.topClock.bonus = +timeString.split('+')[1];
        this.bottomClock.bonus = +this.topClock.bonus;
      }
    },
    handleTimer(side) {
      this.paused = false;

      if (side === 'bottom') {
        this.topRunning = true;
        this.bottomRunning = false;
        this.bottomNotating = this.gameStarted;
      } else {
        this.topRunning = false;
        this.bottomRunning = true;
        this.topNotating = this.gameStarted;
      }

      if (!this.gameStarted) {
        this.white = side === 'bottom' ? 'top' : 'bottom';
        this.gameStarted = true;
      }
    },
    handlePause() {
      this.paused = !this.paused;
    },
    handleReset(notation) {
      this.whiteMoves = [];
      this.blackMoves = [];
      this.chessJsGame = new Chess();
      this.chessJsGame.loadPgn(notation);
      this.chessJsGame.history().forEach((move, i) => {
        if (i % 2 === 0)
          this.whiteMoves.push(move);
        else
          this.blackMoves.push(move);
      });
    },
    hardReset() {
      window.location.reload();
    },
    setPgnMetadata() {
      const today = new Date;
      this.notationHead = `[Date "${today.getFullYear()}.${(today.getMonth() + 1 + '').padStart(2, '0')}.${(today.getDate() + '').padStart(2, '0')}"]\n`;
      this.notationHead = this.notationHead + '[Annotator "ChessNotepad.com"]\n';
      this.notationHead = this.notationHead + `[TimeControl "${this.timeControl}"]\n`;
      this.notationHead = this.notationHead + '\n';
    },
    handleEnd() {
      const notation = [];

      for(let i = 0; i < this.whiteMoves.length; i++)
        notation.push(`${i+1}. ${this.whiteMoves[i]} ${this.blackMoves[i] || ''}`);
      
      this.notation = this.notationHead + notation.join(' ');
      this.showEnd = !this.showEnd;
    },
    handleSettings() {
      this.showSettings = !this.showSettings;
      if (this.showSettings)
        this.showInfo = false;
    }, 
    handleInfo() {
      this.showInfo = !this.showInfo;
      if (this.showInfo)
        this.showSettings = false;
    }, 
    handleNotation(side, n) {

      if (this.white === side)
        this.whiteMoves.push(n);
      else
        this.blackMoves.push(n);

      this.notationTurn = this.notationTurn === 'white' ? 'black' : 'white';

      if (side === 'top')
        this.topNotating = false;
      else
        this.bottomNotating = false;

      if (this.pendingMove) {
        this.validate(this.pendingMove);
        this.pendingMove = null;
      }
      this.topCramped = false;
      this.bottomCramped = false;
    },
    handleNewGame(time) {
      this.timeControl = time;
      this.hardReset();
      this.setClocks(time);
      this.showInfo = false;
    },
    validate(move) {
      if (this.notationTurn !== move.color) {
        if (move.color === 'white')
          this.whiteIsValid = null;
        else
          this.blackIsValid = null;
        this.pendingMove = move;
        return;
      }
      try {
        this.chessJsGame.move(move.notation);
        if (move.color === 'white')
          this.whiteIsValid = true;
        else
          this.blackIsValid = true;
      }
      catch (error) {
        if (move.color === 'white')
          this.whiteIsValid = false;
        else
          this.blackIsValid = false;
      }
    },
    handleCramped(side, isCramped) {
      if (isCramped) {
        if (side === 'top')
          this.topCramped = true;
        else
          this.bottomCramped = true;
      }else {
        if (side === 'top')
          this.topCramped = false;
        else
          this.bottomCramped = false;
      }
    }
  }
}
</script>

<style>

.almost-half {
  height: 45dvh;
  position: relative;
}

.chess-controls {
  height: 10dvh;
}

.flip {
  transform: scale(-1);
}

</style>